import ThrowsValidationError from "common/http/throwsValidationError";
import Address from "domain/address";
import Commodity from "domain/commodity";
import Merchant, { NotificationSettings } from "domain/merchant";
import PhysicalLocation from "domain/physicalLocation";
import AddMerchantRequest from "./addMerchantRequest";
import ContractTypeSetting from "./general/contractTypeSetting";
import AdminHttpService from "admin/common/adminHttpService";
import { ContractType } from "domain/contract";
import { DeliveryMethodDetails } from "domain/deliveryMethod";
import PaymentOptionDetails from "domain/paymentOptionDetails";

class MerchantService extends AdminHttpService {

    async listMerchants(): Promise<Merchant[]> {
        const response = await this.getClient().get<Merchant[]>('/settings/v1/merchants');
        return response.data;
    }

    async getMerchant(merchantId: string): Promise<Merchant> {
        const response = await this.getClient().get<Merchant>(`/settings/v1/merchants/${merchantId}`);
        return response.data;
    }

    @ThrowsValidationError()
    async createMerchant(request: AddMerchantRequest): Promise<Merchant> {
        const response = await this.getClient().post<Merchant>('/settings/v1/merchants', {
            merchantName: request.name,
            merchantEmail: request.email,
            merchantPhone: request.phone,
            merchantWebsiteUrl: request.websiteUrl,
            merchantAdmin: {
                firstName: request.admin.firstName,
                lastName: request.admin.lastName,
                email: request.admin.email,
                phone: request.admin.phone,
            },
            mailingAddress: request.address,
            fcmAccounts: request.fcmAccounts,
            additionalAdmins: request.additionalAdmins,
            giveUpFee: request.giveUpFee,
        });
        return response.data;
    }

    @ThrowsValidationError()
    async updateMerchant(merchantId: string, request: UpdateMerchantRequest): Promise<void> {
        await this.getClient(merchantId).put(`/settings/v1/merchants/${merchantId}`, request);
    }

    async listContractTypeSettings(merchantId: string): Promise<ContractTypeSetting[]> {
        const response = await this.getClient(merchantId).get(`/settings/v1/merchants/${merchantId}/contract-type-settings`);

        return response.data;
    }

    @ThrowsValidationError()
    async updateContractTypeSettings(merchantId: string, settings: ContractTypeSetting[]): Promise<any> {
        await this.getClient(merchantId).put(`/settings/v1/merchants/${merchantId}/contract-type-settings`, {
            settings: settings
        });
    }

    async listDeliveryMethods(merchantId: string): Promise<DeliveryMethodDetails[]> {
        const response = await this.getClient(merchantId).get(`/settings/v1/merchants/${merchantId}/delivery-methods`);

        return response.data;
    }

    @ThrowsValidationError()
    async updateDeliveryMethod(merchantId: string, deliveryMethodDetails: DeliveryMethodDetails): Promise<any> {
        await this.getClient(merchantId).put(`/settings/v1/merchants/${merchantId}/delivery-methods/${deliveryMethodDetails.deliveryMethod}`, {
            name: deliveryMethodDetails.name,
            defaultDeliveryTerms: deliveryMethodDetails.defaultDeliveryTerms,
        });
    }

    async listCommodities(merchantId: string): Promise<Commodity[]> {
        const response = await this.getClient(merchantId).get<Commodity[]>("/settings/v1/commodities", {
            params: {
                results: "mine"
            }
        });
        return response.data;
    }

    async listLocations(merchantId: string): Promise<PhysicalLocation[]> {
        const response = await this.getClient(merchantId).get<PhysicalLocation[]>('/settings/v1/locations');
        return response.data;
    }

    async listPaymentOptions(merchantId: string): Promise<PaymentOptionDetails[]> {
        const response = await this.getClient(merchantId).get<PaymentOptionDetails[]>(`/settings/v1/merchants/${merchantId}/payment-options`);
        return response.data;
    }

    async upsertPaymentOption(merchantId: string, paymentOptionDetails: PaymentOptionDetails): Promise<void> {
        await this.getClient(merchantId).put(`/settings/v1/merchants/${merchantId}/payment-options`, paymentOptionDetails);
    }
}

export interface UpdateMerchantRequest {
    merchantName?: string;
    isPaymentOptionHidden?: boolean;
    isCommentDeletionAllowed?: boolean;
    displayCustomerContractIdForSell?: boolean;
    displayCustomerContractIdForBuy?: boolean;
    makeCustomerContractIdRequired?: boolean;
    useCustomerContractIdAsErpId?: boolean;
    isDetermineLaterAllowed?: boolean;
    displayRegionOnAddOffer?: boolean;
    displayContractLocation?: boolean;
    marketAutoOfferEnabled?: boolean;
    endOfDay?: string;
    mailingAddress?: Address;
    merchantPhone?: string;
    merchantEmail?: string;
    merchantWebsiteUrl?: string;
    enableRiskServiceBypass?: boolean;
    merchantAppStoreUrl?: string;
    merchantPlayStoreUrl?: string;
    giveUpFee?: number;
    useCustomFields?: boolean;
    featureFlags?: { code: string, value: boolean }[];
    contractTypes?: ContractType[];
    notificationSettings?: NotificationSettings;
}

const merchantService = new MerchantService();
export default merchantService;